// R1b-δ — Compliance Reports (Owner-only). Downloads a CSV slice of the
// audit ledger with sha256 attached; every export is audit-logged.
import React, { useState } from "react";
import { api, formatApiError } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { toast } from "sonner";
import { Download } from "lucide-react";

export default function AuditComplianceReportsTab({ isOwner }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);

  const download = async () => {
    if (!isOwner) return;
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (start) params.set("start", start);
      if (end) params.set("end", end);
      const r = await api.get(`/audit/export-compliance-report?${params}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `erf-audit-${(start || "all")}_${(end || "now")}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Compliance report exported · audit-logged");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Export failed.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="audit-compliance-reports-body">
      <div className="mb-4">
        <div className="font-display font-semibold text-erf-dark text-lg">Compliance Reports</div>
        <div className="text-xs font-nav text-erf-dark/60 mt-0.5">CSV export of the audit ledger. Every export is audit-logged with a sha-256 of the returned bytes.</div>
      </div>

      <div className="rounded-md border border-erf-borderCream bg-white p-4">
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label className="text-xs uppercase font-nav">Start (ISO 8601 or blank)</Label>
            <Input value={start} onChange={(e) => setStart(e.target.value)} placeholder="2026-01-01T00:00:00Z" className="mt-1 font-mono" data-testid="compliance-start" />
          </div>
          <div>
            <Label className="text-xs uppercase font-nav">End (ISO 8601 or blank)</Label>
            <Input value={end} onChange={(e) => setEnd(e.target.value)} placeholder="2026-12-31T23:59:59Z" className="mt-1 font-mono" data-testid="compliance-end" />
          </div>
        </div>
        <Button
          onClick={download}
          disabled={busy || !isOwner}
          className="mt-4 bg-erf-gold text-erf-dark hover:bg-erf-goldHover"
          data-testid="compliance-export-btn"
        >
          <Download className="w-4 h-4 mr-1" /> {busy ? "Exporting…" : "Export CSV"}
        </Button>
        {!isOwner && (
          <div className="mt-3 text-[11px] font-mono text-erf-dark/60" data-testid="compliance-admin-note">
            Compliance exports are Owner-only. Administrators can view the Ledger + Chain Verification tabs.
          </div>
        )}
      </div>
    </div>
  );
}
